import { useStore } from '@/providers/app-store-provider';
import { type DependencyList, type EffectCallback, useEffect } from 'react';
import { useCurrencySettled } from '@/hooks/computed';

/**
 * 仅在 `ready` 为 true 时执行 effect（含清理函数），用于把「前置条件就绪」与依赖数组组合在一起。
 */
export function useWhenReady(ready: boolean, effect: EffectCallback, deps: DependencyList = []) {
  useEffect(() => {
    if (!ready) return;
    return effect();
    // 与 `useEffect(callback, deps)` 相同：`deps` 由调用方维护，并额外用 `ready` 做门闩
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 同上
  }, [ready, ...deps]);
}

/**
 * 在站点货币配置就绪（`currencyReady`）后执行 effect，等价于原 `useEffectWithCurReady`。
 */
export function useWhenCurReady(effect: EffectCallback, deps: DependencyList = []) {
  const currencyReady = useCurrencySettled();
  useWhenReady(currencyReady, effect, deps);
}

/**
 * 在客户端完成当前路由轮次下从 cookie 同步 token（`clientTokenReady`，含确认无 token）后再执行 effect。
 * 与 `userSlice` 中说明一致：用于避免子组件在同步完成前误判登录态。
 */
export function useWhenTokenReady(effect: EffectCallback, deps: DependencyList = []) {
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  useWhenReady(clientTokenReady, effect, deps);
}

/**
 * 在货币配置就绪且客户端 token 同步完成（`currencyReady && clientTokenReady`）后再执行 effect。
 */
export function useWhenCurTokenReady(effect: EffectCallback, deps: DependencyList = []) {
  const currencyReady = useCurrencySettled();
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  useWhenReady(currencyReady && clientTokenReady, effect, deps);
}

export function useWhenCurUserInfoReady(effect: EffectCallback, deps: DependencyList = []) {
  const userInfoReady = useStore((state) => state.userInfoReady);
  const currencyReady = useCurrencySettled();
  useWhenReady(userInfoReady && currencyReady, effect, deps);
}
